/**
 * useWizardState — aggregates the setup-master wizard's per-stage state.
 *
 * Phase 1 (#47):
 *   - STAGE 1 (jetson onboarding): WIRED — reads
 *     `getZoneLocalComputer(zoneId)` with 5s polling while non-terminal.
 *     Stage status is purely derived from LC.setup_stage + is_archived.
 *   - STAGES 2-6: still mock (TODO comments mark the swap-in sites).
 *
 * State is purely derived from BE reads (no client cursor, no
 * localStorage) — refreshing the page reconverges to the same view,
 * which makes the three entry-state shapes (factory-fresh /
 * re-attach / cross-server migration) collapse onto one UI.
 */

import useSWR from 'swr';
import {
  SETUP_STAGES_ORDERED,
  getZoneLocalComputer,
  type LocalComputerSummary,
} from '@/api/localComputers';
import type { ZoneDto } from '@/api/zones';

// ── public types ────────────────────────────────────────────────────

export type StageStatus =
  | 'pending'        // not started — upstream may or may not be done
  | 'in_progress'   // actively progressing (polling)
  | 'blocked'        // waiting on an upstream stage / external action
  | 'error'          // something failed; needs retry
  | 'done';          // all sub-steps green

export type StageKey =
  | 'jetson'         // STAGE 1: curl onboarding → DOCKER_RUNNING
  | 'equipment'      // STAGE 2: router + NVR + cameras discovered
  | 'zoning'         // STAGE 3: racks + checkout zones zoned
  | 'grafana'        // STAGE 4: dashboards spawned + URLs set
  | 'notifications'  // STAGE 5: chat IDs + bot membership + pinned message
  | 'handoff';       // STAGE 6: PM invite + Grafana grants + customer chat invite

export interface StageState {
  key: StageKey;
  status: StageStatus;
  /** One-line summary rendered in the card header. Empty string suppresses. */
  summary: string;
  /** i18n params for the summary key, if any. */
  summaryParams?: Record<string, string | number>;
  /** When `blocked`: human-readable why (which upstream stage). */
  blockedReason?: string;
  /** When `error`: short diagnostic surfaced inside the card body. */
  errorDetail?: string;
}

export interface WizardState {
  /** Currently selected zone — null when none chosen yet. */
  zone: ZoneDto | null;
  /**
   * The LocalComputer bound to this zone, if any. Null when:
   * - no zone is selected,
   * - the zone has no bound LC (factory-fresh state — STAGE 1 will
   *   surface an "Issue first curl token" CTA),
   * - the initial fetch is still in flight.
   */
  lc: LocalComputerSummary | null;
  /** True while any underlying read is in flight on initial load. */
  isLoading: boolean;
  /** Stage states ordered top-to-bottom in the page. */
  stages: StageState[];
  /** Force-refresh the LC fetch (e.g. after issuing a token). */
  refreshLc: () => Promise<unknown>;
}

// ── stage ordering ──────────────────────────────────────────────────

export const STAGE_KEYS_ORDERED: readonly StageKey[] = [
  'jetson',
  'equipment',
  'zoning',
  'grafana',
  'notifications',
  'handoff',
] as const;

// ── hook ────────────────────────────────────────────────────────────

const POLL_INTERVAL_MS = 5000;
// SWR returns 0 from refreshInterval to disable polling; non-zero to enable.
const NO_POLL = 0;

export function useWizardState(zone: ZoneDto | null): WizardState {
  const zoneId = zone?.id ?? null;
  const swrKey = zoneId ? `wizard:zone-lc:${zoneId}` : null;

  const { data: lc, error, isLoading, mutate } = useSWR<
    LocalComputerSummary,
    { response?: { status?: number } }
  >(
    swrKey,
    () => getZoneLocalComputer(zoneId!).then(r => r.data),
    {
      // Poll while the LC's setup is non-terminal (curl flow in flight).
      // SWR can return 0 from refreshInterval to disable next tick — naturally
      // winds down once setup_stage === 'DOCKER_RUNNING'.
      refreshInterval: latest =>
        latest && latest.setup_stage !== 'DOCKER_RUNNING' && !latest.is_archived
          ? POLL_INTERVAL_MS
          : NO_POLL,
      shouldRetryOnError: false,
      revalidateOnFocus: true,
    },
  );

  const refreshLc = () => mutate();
  const lcNotFound = error?.response?.status === 404;

  if (!zone) {
    return {
      zone: null,
      lc: null,
      isLoading: false,
      stages: STAGE_KEYS_ORDERED.map(key => ({
        key,
        status: 'blocked',
        summary: '',
        blockedReason: 'wizard.blockedReason.noZone',
      })),
      refreshLc,
    };
  }

  const stages: StageState[] = [
    computeJetsonStage(lc ?? null, lcNotFound, isLoading),
    // STAGES 2-5: still mock until their per-stage wiring lands.
    {
      key: 'equipment',
      status: lc?.setup_stage === 'DOCKER_RUNNING' ? 'pending' : 'blocked',
      summary: '',
      blockedReason:
        lc?.setup_stage === 'DOCKER_RUNNING'
          ? undefined
          : 'wizard.blockedReason.waitForDockerRunning',
    },
    {
      key: 'zoning',
      status: 'blocked',
      summary: '',
      blockedReason: 'wizard.blockedReason.waitForCameras',
    },
    {
      key: 'grafana',
      status: 'pending',
      summary: 'wizard.summary.grafana.empty',
    },
    {
      key: 'notifications',
      status: 'pending',
      summary: 'wizard.summary.notifications.empty',
    },
    {
      key: 'handoff',
      status: 'pending',
      summary: 'wizard.summary.handoff.empty',
    },
  ];

  return {
    zone,
    lc: lc ?? null,
    isLoading: isLoading && !lc && !error,
    stages,
    refreshLc,
  };
}

// ── per-stage computation ───────────────────────────────────────────

function computeJetsonStage(
  lc: LocalComputerSummary | null,
  lcNotFound: boolean,
  isInitialLoading: boolean,
): StageState {
  // No LC bound to this zone — operator needs to issue the first curl token.
  // This is the "factory-fresh" entry shape (a).
  if (lcNotFound || (!lc && !isInitialLoading)) {
    return {
      key: 'jetson',
      status: 'pending',
      summary: 'wizard.summary.jetson.noLc',
    };
  }
  // Initial load: not enough info yet — render as blocked-ish without
  // committing to a stage status. The page's header spinner already covers
  // the loading affordance.
  if (!lc) {
    return {
      key: 'jetson',
      status: 'pending',
      summary: '',
    };
  }

  // Archived LC — needs operator attention; this should not normally happen
  // on a wizard surface (archived rows are typically retired jetsons).
  if (lc.is_archived) {
    return {
      key: 'jetson',
      status: 'error',
      summary: 'wizard.summary.jetson.archived',
      errorDetail: 'wizard.error.jetson.archived',
    };
  }

  const total = SETUP_STAGES_ORDERED.length;
  const idx = SETUP_STAGES_ORDERED.indexOf(lc.setup_stage);
  const doneCount = idx + 1;

  if (lc.setup_stage === 'DOCKER_RUNNING') {
    return {
      key: 'jetson',
      status: 'done',
      summary: 'wizard.summary.jetson.done',
      summaryParams: { done: doneCount, total },
    };
  }

  return {
    key: 'jetson',
    status: 'in_progress',
    summary: 'wizard.summary.jetson.inProgress',
    summaryParams: { done: doneCount, total },
  };
}
